/** Lyrics (WS2): final lines with sections/performers, raw/masked toggle, source + lock state, candidates,
 * paste-and-lock (published text only, never ASR), refetch, LRC/SRT downloads. Writes send If-Match. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ifMatch, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { fmtMs } from "../../lib/frames";
import { apiFileUrl } from "../../lib/identity";
import { useAction } from "../lib/actions";
import { fmtWhen } from "../lib/time";
import { ActionError, Empty, QueryView, Section, TableScroll } from "../lib/ui";
import { intParam, type Patch } from "../lib/url";

type Lyrics = Schemas["LyricsOut"];

export function LyricsSection({ songId, sp, set }: { songId: number; sp: URLSearchParams; set: (p: Patch, msg?: string) => void }) {
  const qc = useQueryClient();
  const key = qk.entity("song", songId, "lyrics");
  const q = useQuery({ queryKey: key, queryFn: () => unwrap(api.GET("/api/songs/{song_id}/lyrics", { params: { path: { song_id: songId } } })) });
  const raw = sp.get("text") === "raw";
  const cand = intParam(sp.get("cand"));
  const reload = async () => {
    await qc.invalidateQueries({ queryKey: key });
  };
  return (
    <Section id="lyrics" title="Lyrics"
      actions={
        <>
          <span className="ws7-inline" role="group" aria-label="Lyrics text">
            <button type="button" aria-pressed={!raw} data-testid="lyrics-masked" onClick={() => set({ text: undefined }, "Showing masked lyrics")}>Masked</button>
            <button type="button" aria-pressed={raw} data-testid="lyrics-raw" onClick={() => set({ text: "raw" }, "Showing raw lyrics")}>Raw</button>
          </span>
          <a className="button" href={apiFileUrl(`/api/songs/${songId}/lyrics.lrc`, { masked: !raw, download: true })} data-testid="lyrics-lrc">Download LRC</a>
          <a className="button" href={apiFileUrl(`/api/songs/${songId}/lyrics.srt`, { masked: !raw, download: true })} data-testid="lyrics-srt">Download SRT</a>
        </>
      }>
      <QueryView q={q} what="lyrics">
        {(l) => <LyricsBody l={l} songId={songId} raw={raw} cand={cand} set={set} reload={reload} />}
      </QueryView>
    </Section>
  );
}

function LyricsBody({ l, songId, raw, cand, set, reload }: {
  l: Lyrics; songId: number; raw: boolean; cand?: number; set: (p: Patch, msg?: string) => void; reload: () => Promise<void>;
}) {
  const key = qk.entity("song", songId);
  const f = l.final;
  const version = f?.version;
  const lock = useAction(
    (locked: boolean) => unwrap(api.POST("/api/songs/{song_id}/lyrics/lock", { params: { path: { song_id: songId } }, body: { locked }, headers: ifMatch(version) })),
    { label: (locked) => `${locked ? "Lock" : "Unlock"} lyrics`, success: (r) => `Lyrics ${r.locked ? "locked" : "unlocked"} (version ${r.version})`, invalidate: [key] },
  );
  const choose = useAction(
    (c: Schemas["CandidateOut"]) => unwrap(api.POST("/api/songs/{song_id}/lyrics/choose", { params: { path: { song_id: songId } }, body: { candidate_id: c.id, lock: false }, headers: ifMatch(version) })),
    { label: (c) => `Use candidate #${c.id}`, success: (r, c) => `Final lyrics now from candidate #${c.id} (${r.source}); timings regenerate`, invalidate: [key] },
  );
  const refetch = useAction(
    () => unwrap(api.POST("/api/songs/{song_id}/lyrics/refetch", { params: { path: { song_id: songId } } })),
    { label: "Refetch lyrics", success: (r) => `Lyrics refetch queued: jobs ${r.job_ids.map((j) => `#${j}`).join(", ") || "none"}`, invalidate: [key] },
  );
  const err = lock.error ?? choose.error ?? refetch.error;
  return (
    <>
      <p data-testid="lyrics-state">
        {f ? (
          <>Source <strong>{f.source}</strong> · {f.locked ? <strong>locked{f.locked_by ? ` by ${f.locked_by}` : ""}</strong> : "not locked"} · {f.lines.length} lines · timings v{f.timings_version ?? "–"} · updated {fmtWhen(f.updated_at)}</>
        ) : (
          <>No final lyrics yet. Paste published lyrics below or choose a candidate.</>
        )}
      </p>
      <div className="ws7-row">
        {f && (
          <button type="button" data-testid="lyrics-lock" disabled={lock.pending} onClick={() => void lock.run(!f.locked)}>
            {f.locked ? "Unlock lyrics" : "Lock lyrics"}
          </button>
        )}
        <button type="button" data-testid="lyrics-refetch" disabled={refetch.pending} onClick={() => void refetch.run()}>Refetch lyrics</button>
      </div>
      <ActionError error={err} onReload={async () => { await reload(); lock.clear(); choose.clear(); }} />
      {f && f.lines.length > 0 && (
        <TableScroll label="Final lyrics table">
          <table className="ws7-table" aria-label="Final lyrics" data-testid="lyrics-lines">
            <thead><tr><th scope="col" className="num">#</th><th scope="col">Start</th><th scope="col">Section</th><th scope="col">Performer</th><th scope="col">Text ({raw ? "raw" : "masked"})</th></tr></thead>
            <tbody>
              {f.lines.map((ln) => (
                <tr key={ln.idx} className={`ws7-lyrics-line${ln.empty ? " empty" : ""}`}>
                  <td className="num">{ln.idx}</td>
                  <td className="ws7-mono">{ln.t0_song_ms != null ? fmtMs(ln.t0_song_ms) : ""}</td>
                  <td>{ln.section ?? ""}</td>
                  <td>{ln.performer ?? ""}</td>
                  <td>{ln.empty ? "(unpublished — stays empty)" : raw ? ln.text_raw : ln.text_masked}{ln.adlib ? <span className="ws7-muted"> (ad-lib)</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      <h3>Candidates</h3>
      {l.candidates.length === 0 ? <Empty>No candidates fetched.</Empty> : (
        <TableScroll label="Candidates table">
          <table className="ws7-table" aria-label="Lyrics candidates" data-testid="lyrics-candidates">
            <thead><tr><th scope="col" className="num">Rank</th><th scope="col">Source</th><th scope="col">Kind</th><th scope="col" className="num">Lines</th>
              <th scope="col">Duration match</th><th scope="col">Similarity</th><th scope="col">Usable</th><th scope="col">Actions</th></tr></thead>
            <tbody>
              {l.candidates.map((c) => (
                <tr key={c.id} data-testid={`candidate-${c.id}`}>
                  <td className="num">{c.rank}</td>
                  <td>{c.source} <span className="ws7-muted">{c.source_ref}</span></td>
                  <td>{c.kind}</td>
                  <td className="num">{c.line_count}</td>
                  <td>{c.duration_match == null ? "–" : c.duration_match ? "yes" : "no"}</td>
                  <td>{c.similarity != null ? c.similarity.toFixed(2) : "–"}</td>
                  <td>{c.usable ? "yes" : `no${c.reason ? `: ${c.reason}` : ""}`}</td>
                  <td>
                    <span className="ws7-inline">
                      <button type="button" aria-pressed={cand === c.id} aria-label={`${cand === c.id ? "Hide" : "Show"} text of candidate #${c.id}`}
                        onClick={() => set({ cand: cand === c.id ? undefined : c.id })}>
                        {cand === c.id ? "Hide" : "Show"} text
                      </button>
                      <button type="button" data-testid={`candidate-use-${c.id}`} aria-label={`Use candidate #${c.id}`} disabled={choose.pending || !c.usable}
                        onClick={() => void choose.run(c)}>
                        Use
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {cand != null && l.candidates.find((c) => c.id === cand) && (
        <pre className="ws7-pre" tabIndex={0} aria-label={`Text of candidate #${cand}`} data-testid="candidate-text">{l.candidates.find((c) => c.id === cand)?.text}</pre>
      )}
      <h3>Paste published lyrics</h3>
      <PasteForm songId={songId} version={version} reload={reload} />
    </>
  );
}

function PasteForm({ songId, version, reload }: { songId: number; version?: number; reload: () => Promise<void> }) {
  const [text, setText] = useState("");
  const [source, setSource] = useState<"paste" | "label">("label");
  const [ref, setRef] = useState("");
  const [lockIt, setLockIt] = useState(true);
  const paste = useAction(
    () => unwrap(api.POST("/api/songs/{song_id}/lyrics/paste", { params: { path: { song_id: songId } }, body: { text, source, source_ref: ref, lock: lockIt }, headers: ifMatch(version) })),
    { label: "Paste lyrics", success: (r) => `Pasted ${r.lines.length} lines${r.locked ? " and locked them" : ""}; timings regenerate`, invalidate: [qk.entity("song", songId)] },
  );
  return (
    <form className="ws7-form" aria-label="Paste lyrics" onSubmit={async (e) => {
      e.preventDefault();
      if (await paste.run()) setText("");
    }}>
      <label className="ws7-field">
        Published lyrics (with [Section: performer] headers)
        <textarea value={text} maxLength={50000} data-testid="paste-text" onChange={(e) => setText(e.target.value)} />
      </label>
      <div className="ws7-toolbar">
        <label className="ws7-field">
          Source
          <select value={source} data-testid="paste-source" onChange={(e) => setSource(e.target.value as "paste" | "label")}>
            <option value="label">label</option>
            <option value="paste">paste</option>
          </select>
        </label>
        <label className="ws7-field">
          Source reference
          <input value={ref} maxLength={300} data-testid="paste-ref" onChange={(e) => setRef(e.target.value)} />
        </label>
        <label className="ws7-check">
          <input type="checkbox" checked={lockIt} data-testid="paste-lock" onChange={(e) => setLockIt(e.target.checked)} />
          Lock after paste
        </label>
        <button type="submit" className="ws7-primary" data-testid="paste-submit" disabled={!text.trim() || paste.pending}>
          {lockIt ? "Paste and lock" : "Paste"}
        </button>
      </div>
      <ActionError error={paste.error} onReload={async () => { await reload(); paste.clear(); }} />
    </form>
  );
}
